import React, { useState, useCallback } from "react";
import { createDescendantContext } from "@chakra-ui/descendant";
import { useKeyPressEvent } from "react-use";
import { Flex, FlexProps, Input, List, Portal, StackProps, Text, useMenu, VStack } from "@chakra-ui/react";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { PropertySearchMenuItem } from "./PropertySearchMenuItem";
import { useHotkeys } from "react-hotkeys-hook";
import { Property } from "@tether-web-portal/types/Property";

const backdropVariants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      type: "tween",
      duration: 0.15,
      ease: "easeOut",
    },
  },
  enter: {
    opacity: 1,
    transition: {
      type: "tween",
      duration: 0.15,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      type: "tween",
      duration: 0.15,
      ease: "easeOut",
    },
  },
};

const commandPaletteVariants: Variants = {
  initial: {
    scale: 0.9,
    transition: {
      type: "tween",
      duration: 0.15,
      ease: "easeOut",
    },
  },
  enter: {
    scale: 1,
    transition: {
      type: "tween",
      duration: 0.15,
      ease: "easeOut",
    },
  },
  exit: {
    scale: 0.9,
    transition: {
      type: "tween",
      duration: 0.15,
      ease: "easeOut",
    },
  },
};

const MotionFlex = motion<FlexProps>(Flex);
const MotionVStack = motion<StackProps>(VStack);

const [
  MenuDescendantsProvider,
  useMenuDescendantsContext,
  useMenuDescendants,
  useMenuDescendant,
] = createDescendantContext<HTMLElement>();

interface KeyboardMenuDemoProps {
  /** The server's matches for the current term — already filtered, so do not filter again. */
  properties: Property[];
  /** Reports what was typed so the caller can ask the server. */
  onSearchChange?: (search: string) => void;
  /** The rows belong to a different term than the one in the box — do not act on them. */
  resultsAreStale?: boolean;
  /** The search request FAILED — distinct from "nothing matched", which it looks like. */
  searchError?: boolean;
  onItemSelected: (item: Property) => void;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export const GloablPropertySearchModal = ({
  properties,
  onSearchChange,
  resultsAreStale = false,
  searchError = false,
  onItemSelected,
  isOpen,
  onClose,
  onOpen,
}: KeyboardMenuDemoProps) => {
  const searchRef = React.useRef<HTMLInputElement>(null);

  const [searchText, setSearchText] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const { descendants } = useMenu();

  const close = useCallback(() => {
    onClose();
    setFocusedIndex(0);
    setSearchText("");
    // The OWNER of the search term has to be told too. Resetting only the local input left
    // the parent holding the last query, so the next Cmd+K showed an empty box above the
    // previous term's matches — and Enter opened a property nobody searched for.
    onSearchChange?.("");
  }, [onClose, setFocusedIndex, onSearchChange]);

  useHotkeys("ctrl+k, cmd+k", (e: KeyboardEvent) => {
    // stop propagation to fix linux
    e.preventDefault();
    e.stopPropagation();
    onOpen();
  });

  useKeyPressEvent("ArrowDown", () => {
    const next = descendants.nextEnabled(focusedIndex);
    if (next) {
      setFocusedIndex(next.index);
      next.node.focus();
      next.node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  });

  useKeyPressEvent("ArrowUp", () => {
    const prev = descendants.prevEnabled(focusedIndex);
    if (prev) {
      setFocusedIndex(prev.index);
      prev.node.focus();
      prev.node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  });

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    },
    [close]
  );

  // No local filter: `properties` IS the server's answer for the current term. Filtering
  // a page of matches again would hide rows that matched, which looks like a search that
  // does not work.
  //
  // But rows for a PREVIOUS term must not be selectable — see `resultsAreStale`.
  const matchingProperties = resultsAreStale ? [] : properties.slice(0, 8);

  return (
    <AnimatePresence exitBeforeEnter>
      {isOpen && (
        <Portal>
          <MotionFlex
            onClick={close}
            variants={backdropVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            position="fixed"
            inset={0}
            bg="whiteAlpha.800"
            _dark={{
              bg: "blackAlpha.800",
            }}
            zIndex="tooltip"
            alignItems="flex-start"
            justifyContent="center"
          >
            <MotionVStack
              variants={commandPaletteVariants}
              initial="initial"
              animate="enter"
              exit="exit"
              mt={{ base: 12, md: 24 }}
              mx={2}
              onClick={(e) => e.stopPropagation()}
              bg="white"
              _dark={{
                bg: "gray.900",
              }}
              shadow="2xl"
              maxW="3xl"
              maxH={{ base: "40vh", md: "md" }}
              overflow="hidden"
              w="full"
              rounded="md"
              borderWidth="1px"
              borderStyle="solid"
            >
              <>
                <Input
                  placeholder="Search for property to open"
                  ref={searchRef}
                  autoFocus
                  value={searchText}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  minHeight="40px" // Stop height changing while typing
                />
                <List overflow="auto" w="full" pb={2} px={2}>
                  {Boolean(resultsAreStale && searchText.trim()) && (
                    // Said out loud rather than showing the previous term's rows: this
                    // window is 300ms of debounce plus a round trip, and Enter inside it
                    // used to open a property matching a prefix of what was typed.
                    <Text color="gray.500" fontSize="sm" px={2} py={1}>
                      Searching…
                    </Text>
                  )}
                  {Boolean(!resultsAreStale && searchError && searchText.trim()) && (
                    <Text color="red.500" fontSize="sm" px={2} py={1}>
                      Couldn&apos;t search — try again.
                    </Text>
                  )}
                  {Boolean(
                    !resultsAreStale && !searchError && searchText.trim() && matchingProperties.length === 0,
                  ) && (
                    <Text color="gray.500" fontSize="sm" px={2} py={1}>
                      No properties match &ldquo;{searchText.trim()}&rdquo;.
                    </Text>
                  )}
                  <MenuDescendantsProvider value={descendants}>
                    {matchingProperties.map((property, index) => (
                      <PropertySearchMenuItem
                        key={index}
                        property={property}
                        onClick={() => {
                          onItemSelected(property);
                          close();
                        }}
                        focusedIndex={focusedIndex}
                      />
                    ))}
                  </MenuDescendantsProvider>
                </List>
              </>
            </MotionVStack>
          </MotionFlex>
        </Portal>
      )}
    </AnimatePresence>
  );
};
